import { ChangeDetectionStrategy, Component, ElementRef, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router, UrlTree } from '@angular/router';
import { AuthService } from 'app/core/services/auth/auth.service';
import { IdentidadePortalService } from 'app/features/portal-carta-servico/services/identidade-portal.service';
import {
  ItemMenuPortal,
  MenuCabecalhoPublicoService,
} from 'app/features/portal-carta-servico/services/menu-cabecalho-publico.service';
import { ConfirmacaoSaidaService } from 'app/shared/utils/confirmacao-saida.service';

/**
 * Para onde um item do menu leva, nos dois menus. Interno vira `UrlTree`, para `?` e `#`
 * funcionarem no `routerLink`; externo é `href`.
 */
interface HeaderDestino {
  destino: UrlTree | null;
  href: string | null;
  novaAba: boolean;
}

interface HeaderMenuItem extends HeaderDestino {
  /** `track` do `@for` e chave do dropdown aberto. */
  chave: string;
  label: string;
  linha: string;
  exato: boolean;
  /** Nulo no item que navega; os subitens no dropdown. */
  filhos: readonly HeaderSubItem[] | null;
}

interface HeaderSubItem extends HeaderDestino {
  label: string;
}

/** Um item do menu padrão: o caminho inteiro, com a consulta. O dropdown não tem link. */
interface ItemMenuBase {
  id: string;
  label: string;
  link: string | null;
  filhos?: readonly { label: string; link: string }[];
}

interface AtalhoAreaCidadao {
  label: string;
  aba: string;
  icone: string;
}

const ICONES = 'assets/images/icones';

/** A linha embaixo de cada item principal, pela posição: azul, vermelha, verde, amarela, e repete. */
const LINHAS = ['azul', 'vermelha', 'verde', 'amarela'].map((cor) => `${ICONES}/linha-${cor}.svg`);

/** Só no menu padrão: o menu personalizado leva aonde o órgão cadastrou, logado ou não. */
const DESTINO_AUTENTICADO: Readonly<Record<string, string>> = {
  agendamento: '/area-cidadao?aba=agendamento',
  acompanhamento: '/area-cidadao?aba=solicitacoes',
};

const ATALHOS_AREA_CIDADAO: readonly AtalhoAreaCidadao[] = [
  { label: 'Agendamento', aba: 'agendamento', icone: 'calendar' },
  { label: 'Meus agendamentos', aba: 'meus-agendamentos', icone: 'calendar-check' },
  { label: 'Minhas solicitações', aba: 'solicitacoes', icone: 'file-text' },
  { label: 'Dados pessoais', aba: 'dados', icone: 'user' },
  { label: 'Serviços favoritos', aba: 'favoritos', icone: 'heart' },
];

/**
 * O menu padrão, de todo órgão que não publicou um menu personalizado no adm (ou publicou
 * sem item ativo). O `MENU_PADRAO` da tela Cabeçalho do adm tem os mesmos itens e os mesmos
 * destinos de visitante: os dois precisam continuar iguais.
 */
const MENU_BASE: readonly ItemMenuBase[] = [
  { id: 'inicio', label: 'Início', link: '/' },
  {
    id: 'servicos',
    label: 'Serviços',
    link: null,
    filhos: [
      { label: 'Serviços digitais', link: '/servicos-digitais' },
      { label: 'Todos serviços', link: '/servicos' },
    ],
  },
  { id: 'acompanhamento', label: 'Acompanhamento de Solicitação', link: '/acompanhamento' },
  { id: 'agendamento', label: 'Agendamento Presencial', link: '/login' },
  { id: 'ouvidoria', label: 'Ouvidoria', link: '/ouvidoria' },
  { id: 'faleConosco', label: 'Fale Conosco', link: '/outros-canais-atendimento' },
];

function linhaDa(indice: number): string {
  return LINHAS[indice % LINHAS.length];
}

@Component({
  selector: 'app-main-header',
  templateUrl: './main-header.component.html',
  styleUrls: ['./main-header.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  host: {
    '(document:click)': 'aoClicarFora($event)',
    '(document:keydown.escape)': 'fecharMenu()',
  },
})
export class MainHeaderComponent {
  private readonly router = inject(Router);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly auth = inject(AuthService);
  private readonly confirmacaoSaida = inject(ConfirmacaoSaidaService);
  private readonly identidade = inject(IdentidadePortalService);
  private readonly menuPublico = inject(MenuCabecalhoPublicoService);

  readonly atalhosAreaCidadao = ATALHOS_AREA_CIDADAO;

  readonly menuAberto = signal(false);
  /** A `chave` do dropdown aberto: um por vez. */
  readonly dropdownAberto = signal<string | null>(null);
  readonly contaAberta = signal(false);
  readonly brasaoIndisponivel = signal(false);

  readonly nome = this.identidade.nome;
  readonly nomeCurto = this.identidade.nomeCurto;

  readonly brasaoUrl = computed(() => (this.brasaoIndisponivel() ? null : this.identidade.brasaoUrl()));

  /** O brasão fica dentro do link com o nome: com o nome, a imagem não repete nada. */
  readonly altBrasao = computed(() => (this.nome() ? '' : 'Brasão'));

  private readonly usuario = toSignal(this.auth.currentUser, {
    initialValue: this.auth.currentUserValue,
  });

  readonly autenticado = computed(() => !!this.usuario() && !this.auth.isTokenExpired());

  /**
   * Carregando, nenhum item: o padrão não aparece para ser trocado pelo personalizado um
   * instante depois. Com itens na API, o menu personalizado; sem itens ou com erro, o padrão.
   */
  readonly menuItens = computed<readonly HeaderMenuItem[]>(() => {
    if (this.menuPublico.carregando()) {
      return [];
    }

    const personalizado = this.menuPublico.itens();

    if (personalizado.length > 0) {
      return personalizado.map((item, indice) => this.itemPersonalizado(item, indice));
    }

    const autenticado = this.autenticado();

    return MENU_BASE.map((item, indice) => this.itemPadrao(item, indice, autenticado));
  });

  toggleMenu(): void {
    this.menuAberto.update((aberto) => !aberto);
    if (!this.menuAberto()) {
      this.dropdownAberto.set(null);
      this.contaAberta.set(false);
    }
  }

  fecharMenu(): void {
    this.menuAberto.set(false);
    this.dropdownAberto.set(null);
    this.contaAberta.set(false);
  }

  toggleDropdown(chave: string, event: Event): void {
    event.preventDefault();
    this.dropdownAberto.update((aberto) => (aberto === chave ? null : chave));
    this.contaAberta.set(false);
  }

  toggleConta(event: Event): void {
    event.preventDefault();
    this.contaAberta.update((aberto) => !aberto);
    this.dropdownAberto.set(null);
  }

  logar(): void {
    this.fecharMenu();
    this.router.navigateByUrl('/login');
  }

  cadastrar(): void {
    this.fecharMenu();
    this.router.navigateByUrl('/cadastro');
  }

  async sair(): Promise<void> {
    this.fecharMenu();

    if (!(await this.confirmacaoSaida.confirmar())) {
      return;
    }

    this.auth.logout();
    this.router.navigateByUrl('/');
  }

  aoClicarFora(event: MouseEvent): void {
    if (this.host.nativeElement.contains(event.target as Node)) {
      return;
    }

    this.dropdownAberto.set(null);
    this.contaAberta.set(false);
  }

  private itemPadrao(item: ItemMenuBase, indice: number, autenticado: boolean): HeaderMenuItem {
    const link = (autenticado ? DESTINO_AUTENTICADO[item.id] : undefined) ?? item.link;

    return {
      chave: `padrao:${item.id}`,
      label: item.label,
      linha: linhaDa(indice),
      ...this.interno(link),
      exato: link === '/',
      filhos: item.filhos?.map((filho) => ({ label: filho.label, ...this.interno(filho.link) })) ?? null,
    };
  }

  private itemPersonalizado(item: ItemMenuPortal, indice: number): HeaderMenuItem {
    return {
      chave: `item:${indice}`,
      label: item.rotulo,
      linha: linhaDa(indice),
      ...this.destinoDe(item),
      exato: item.interno && item.link === '/',
      filhos: item.subitens.length > 0
        ? item.subitens.map((subitem) => ({ label: subitem.rotulo, ...this.destinoDe(subitem) }))
        : null,
    };
  }

  private destinoDe(item: ItemMenuPortal): HeaderDestino {
    if (!item.link || item.interno) {
      return this.interno(item.link);
    }

    return { destino: null, href: item.link, novaAba: item.novaAba };
  }

  private interno(link: string | null): HeaderDestino {
    return { destino: link ? this.router.parseUrl(link) : null, href: null, novaAba: false };
  }
}
